import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import SuccessPayment from "./success-payment"; 
import axios from "axios";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function PaymentFormInner({ setLoading, setMessage, setPaymentSuccess }) {
  const stripe = useStripe();
  const elements = useElements();

  const [name, setName] = useState(localStorage.getItem("contactName") || "");
  const [email, setEmail] = useState(localStorage.getItem("contactEmail") || "");
  const [contactForm, setContactForm] = useState(null);
  const [userId, setUserId] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const [selectedMedicine, setSelectedMedicine] = useState(null);

  const isFormValid = name.trim() && email.trim() && cardComplete && selectedMedicine;

  // Load selected medicine from localStorage
  useEffect(() => {
    const savedMed = localStorage.getItem("selectedMedicine");
    if (savedMed) setSelectedMedicine(JSON.parse(savedMed));

    const savedForm = localStorage.getItem("contactForm");
    if (savedForm) setContactForm(JSON.parse(savedForm));

    const storedUserId = localStorage.getItem("userId");
    if (storedUserId) setUserId(storedUserId);
  }, []);

  const pay = async () => {
    if (!stripe || !elements || !isFormValid) return false;

    setLoading(true);
    setMessage("");

    try {
      const cardElement = elements.getElement(CardElement);
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        setMessage(error.message);
        setLoading(false);
        return false;
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(
        `${baseUrl}/payments/token-charge`,
        {
          stripe_token: token.id,
          amount: selectedMedicine.price, 
          medicine_id: selectedMedicine.treatmentId, 
          dose: selectedMedicine.selectedDose,      
          address: contactForm?.address,
          user_name: name,
          user_email: email,
          user_id: userId,
          phone: contactForm?.phone,
        },
        { headers: { "Content-Type": "application/json", Accept: "application/json" } }
      );

      if (response.data.success) {
        setMessage("Payment successful!");
        localStorage.clear();
        setPaymentSuccess(true);
      } else {
        setMessage("Payment failed on server side.");
      }

      setLoading(false);
      return response.data.success;
    } catch (error) {
      console.error("Payment error:", error);
      setMessage("Payment failed. Please try again.");
      setLoading(false);
      return false;
    }
  };

  // Expose pay function globally if needed
  useEffect(() => {
    window.stripePay = pay;
    window.stripePayReady = isFormValid;
  }, [stripe, elements, selectedMedicine, name, email, cardComplete]);

  if (!selectedMedicine) {
    return <p className="text-center py-6">Please select a medicine first.</p>;
  }

  return (
    <div className="relative font-poppins max-w-lg mx-auto p-6 bg-white rounded-lg">
      <h2 className="text-[16px] font-[700] text-black mb-4">Payment</h2>

      <div className="mb-4 p-3 bg-gray-100 rounded-md">
        <p className="text-[14px] font-medium text-black">
          Medicine: <span className="font-semibold">{selectedMedicine.name}</span>
        </p>
        <p className="text-[14px] font-medium text-black">
          Dose: <span className="font-semibold">{selectedMedicine.selectedDose}</span>
        </p>
        <p className="text-[14px] font-medium text-black">
          Price: <span className="font-semibold">${selectedMedicine.price}</span>
        </p>
        <p className="text-[14px] text-gray-600">{selectedMedicine.description}</p>
      </div>

      <form>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-[14px] text-black mb-1">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9]"
              required
            />
          </div>
          <div>
            <label className="block text-[14px] text-black mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9]"
              required
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-[14px] text-black mb-1">Card Details</label>
          <div className="border border-gray-200 rounded-md p-3">
            <CardElement onChange={(e) => setCardComplete(e.complete)} />
          </div>
        </div>
      </form>
    </div>
  );
}

export default function Step13Stripe() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState(false); 

  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-24 h-24 border-8 border-gray-200 border-t-8 border-t-[#35C3D9] rounded-full animate-spin"></div>
        </div>
      )}

      {paymentSuccess ? (
        <SuccessPayment />
      ) : (
        <Elements stripe={stripePromise}>
          <PaymentFormInner
            setLoading={setLoading}
            setMessage={setMessage}
            setPaymentSuccess={setPaymentSuccess}
          />
        </Elements>
      )}

      {message && <div className="text-center mt-4 font-medium">{message}</div>}
    </>
  );
}
